import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRouter } from "next/router";
import LoginSignupModal from "./LoginSignupModal";
import SessionExpiredModal from "./SessionExpiredModal";
import { getUserDetails } from "@/services/api/auth";
import { getRegionalHomeHref } from "@/utils/region";
import {
  clearAuthSession,
  clearLegacyLocalStorage,
  getAuthToken,
  getSessionExpiresAt,
  getUserDisplayName,
  isAuthSessionExpired,
  isLoggedIn,
  normalizeUserDetails,
  setAuthSession,
  withRegionPath,
} from "@/utils/session";

const AUTH_ROUTES = ["/login", "/signup", "/register"];

function isPackageBookingPage(pathname = "", asPath = "") {
  const path = String(pathname || "");
  const url = String(asPath || "");

  return (
    path.includes("package-booking") ||
    /\/(holiday|activity)\/package-booking(?:[/?]|$)/.test(url)
  );
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("login");
  const [returnUrl, setReturnUrl] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState("");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionLogoutLoading, setSessionLogoutLoading] = useState(false);

  const syncAuthState = useCallback(() => {
    setLoggedIn(isLoggedIn());
    setUserDisplayName(getUserDisplayName());
    if (!isAuthSessionExpired()) {
      setSessionExpiredOpen(false);
    }
  }, []);

  const checkSessionExpiry = useCallback(() => {
    if (isAuthSessionExpired()) {
      setSessionExpiredOpen(true);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    clearLegacyLocalStorage();
    syncAuthState();
    setAuthReady(true);
  }, [syncAuthState]);

  useEffect(() => {
    if (!loggedIn) {
      setSessionExpiredOpen(false);
      return undefined;
    }

    if (checkSessionExpiry()) return undefined;

    const expiresAt = getSessionExpiresAt();
    if (!expiresAt) return undefined;

    const msUntilExpiry = expiresAt - Date.now();
    let timeoutId;

    if (msUntilExpiry <= 0) {
      checkSessionExpiry();
    } else {
      timeoutId = window.setTimeout(() => {
        setSessionExpiredOpen(true);
      }, msUntilExpiry);
    }

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [loggedIn, checkSessionExpiry]);

  useEffect(() => {
    if (!loggedIn || userDisplayName) return undefined;

    const token = getAuthToken();
    if (!token) return undefined;

    let cancelled = false;

    getUserDetails(token)
      .then((profileResponse) => {
        if (cancelled) return;

        const profile = Array.isArray(profileResponse?.data)
          ? profileResponse.data[0]
          : profileResponse?.data;
        const details = normalizeUserDetails(profile);

        if (details) {
          setAuthSession({ userDetails: details });
          setUserDisplayName(getUserDisplayName());
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [loggedIn, userDisplayName]);

  useEffect(() => {
    if (!router.isReady) return;

    const path = router.pathname;
    if (!AUTH_ROUTES.includes(path)) return;

    if (isLoggedIn()) {
      setOpen(false);
      router.replace(withRegionPath("/"));
      return;
    }

    setMode(path === "/login" ? "login" : "signup");
    setReturnUrl(
      typeof router.query.returnUrl === "string" ? router.query.returnUrl : ""
    );
    setOpen(true);
  }, [router.isReady, router.pathname, router.query.returnUrl, router]);

  const openAuthModal = useCallback(
    ({ mode: nextMode = "login", returnUrl: nextReturnUrl = "" } = {}) => {
      if (isLoggedIn()) {
        return;
      }

      setMode(nextMode);
      setReturnUrl(nextReturnUrl || router.asPath || "");
      setOpen(true);
    },
    [router]
  );

  const closeAuthModal = useCallback(() => {
    setOpen(false);

    if (AUTH_ROUTES.includes(router.pathname)) {
      router.replace(withRegionPath("/"));
    }
  }, [router]);

  const handleSessionExpiredLogout = useCallback(() => {
    setSessionLogoutLoading(true);
    clearAuthSession();
    syncAuthState();
    setSessionExpiredOpen(false);
    window.location.href = getRegionalHomeHref();
  }, [syncAuthState]);

  const handleLoginSuccess = useCallback(() => {
    syncAuthState();
    setSessionExpiredOpen(false);
    setOpen(false);

    if (typeof window === "undefined") return;

    if (isPackageBookingPage(router.pathname, router.asPath)) {
      if (window.history.length > 1) {
        router.back();
      }
      return;
    }

    const destination =
      returnUrl ||
      (AUTH_ROUTES.includes(router.pathname) ? withRegionPath("/") : "");

    if (destination && destination !== router.asPath) {
      router.replace(destination);
    }
  }, [returnUrl, router.asPath, router.pathname, router, syncAuthState]);

  const value = useMemo(
    () => ({
      open,
      mode,
      loggedIn,
      authReady,
      userDisplayName,
      openAuthModal,
      closeAuthModal,
      refreshAuth: syncAuthState,
    }),
    [
      open,
      mode,
      loggedIn,
      authReady,
      userDisplayName,
      openAuthModal,
      closeAuthModal,
      syncAuthState,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <LoginSignupModal
        open={open && !sessionExpiredOpen}
        mode={mode}
        onClose={closeAuthModal}
        onSuccess={handleLoginSuccess}
      />
      <SessionExpiredModal
        open={sessionExpiredOpen}
        loading={sessionLogoutLoading}
        onLogout={handleSessionExpiredLogout}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
